import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type {
  ProcurementRequestDetail,
  ProcurementRequestItemStatus,
} from '../../../shared/api/procurementRequests.api'

// Таблица позиций BOM-запроса с сортировкой по заголовкам. Вынесена из
// ProcurementRequestDetailPage (лимит 500 строк на файл).

const ITEM_STATUS_LABEL: Record<ProcurementRequestItemStatus, string> = {
  PENDING: 'Ждём',
  ANSWERED: 'Ответил',
  REFUSED: 'Нет позиции',
  NO_RESPONSE: 'Не ответил',
}

const ITEM_STATUS_TONE: Record<ProcurementRequestItemStatus, string> = {
  PENDING: 'bg-blue-50 text-blue-700 border-blue-200',
  ANSWERED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REFUSED: 'bg-red-50 text-red-700 border-red-200',
  NO_RESPONSE: 'bg-gray-100 text-gray-600 border-gray-200',
}

// Сортировка таблицы позиций — стиль такой же, как в Аналитике → Менеджеры.
type SortKey = 'partNumber' | 'brand' | 'quantity' | 'responsePrice' | 'status'
type SortDir = 'asc' | 'desc'

// Для статуса фиксируем логический порядок: «хороший» исход → «плохой».
const STATUS_ORDER: Record<ProcurementRequestItemStatus, number> = {
  ANSWERED: 0,
  PENDING: 1,
  NO_RESPONSE: 2,
  REFUSED: 3,
}

type BomItem = ProcurementRequestDetail['items'][number]

const SORT_VALUE: Record<SortKey, (it: BomItem) => number | string> = {
  partNumber: (it) => it.partNumber.toLowerCase(),
  brand: (it) => (it.brand ?? '').toLowerCase(),
  quantity: (it) => it.quantity,
  responsePrice: (it) => (it.responsePrice == null ? -1 : Number(it.responsePrice)),
  status: (it) => STATUS_ORDER[it.status],
}

// Иконка сортировки в стиле AG Grid: одиночная стрелка ↑/↓, только когда колонка активна.
function SortIcon({ active, dir }: { active: boolean; dir: SortDir | null }) {
  if (!active) return null
  return (
    <span className="ml-1 text-gray-700 text-[11px] leading-none">{dir === 'asc' ? '↑' : '↓'}</span>
  )
}

// Декоративная funnel-иконка AG Grid (рядом с заголовком). Кликабельной зоной
// служит весь заголовок — клик по любой точке переключает сортировку.
function FilterIcon() {
  return (
    <svg
      className="ml-1.5 w-3 h-3 text-gray-400 shrink-0"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="1.5" y1="3" x2="10.5" y2="3" />
      <line x1="3.5" y1="6" x2="8.5" y2="6" />
      <line x1="5" y1="9" x2="7" y2="9" />
    </svg>
  )
}

function SortableTh({
  label,
  sortKey,
  current,
  onToggle,
  className = '',
  align = 'left',
}: {
  label: string
  sortKey: SortKey
  current: { key: SortKey; dir: SortDir } | null
  onToggle: (k: SortKey) => void
  className?: string
  align?: 'left' | 'right' | 'center'
}) {
  const active = current?.key === sortKey
  const alignCls =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'
  return (
    <th
      className={`px-3 py-2 text-xs font-medium border-r border-gray-200 last:border-r-0 ${className}`}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center w-full ${alignCls} hover:text-gray-900 ${
          active ? 'text-gray-900' : 'text-gray-600'
        }`}
      >
        <span>{label}</span>
        <SortIcon active={active} dir={active ? current.dir : null} />
        <FilterIcon />
      </button>
    </th>
  )
}

export function BomItemsTable({
  items,
  onPatchStatus,
}: {
  items: BomItem[]
  onPatchStatus: (itemId: string, status: ProcurementRequestItemStatus) => void
}) {
  // Сортировка таблицы позиций: клик по заголовку — desc → asc → выкл.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null)
  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' }
      if (prev.dir === 'desc') return { key, dir: 'asc' }
      return null
    })
  }
  const sortedItems = useMemo(() => {
    if (!sort) return items
    const getter = SORT_VALUE[sort.key]
    const sorted = [...items].sort((a, b) => {
      const va = getter(a)
      const vb = getter(b)
      if (typeof va === 'number' && typeof vb === 'number') return va - vb
      return String(va).localeCompare(String(vb), 'ru')
    })
    return sort.dir === 'desc' ? sorted.reverse() : sorted
  }, [items, sort])

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-10">№</th>
            <SortableTh label="Арт." sortKey="partNumber" current={sort} onToggle={toggleSort} />
            <SortableTh label="Бренд" sortKey="brand" current={sort} onToggle={toggleSort} />
            <SortableTh
              label="Кол-во"
              sortKey="quantity"
              current={sort}
              onToggle={toggleSort}
              className="w-20"
              align="right"
            />
            <SortableTh
              label="Цена ответа"
              sortKey="responsePrice"
              current={sort}
              onToggle={toggleSort}
              className="w-32"
            />
            <SortableTh
              label="Статус"
              sortKey="status"
              current={sort}
              onToggle={toggleSort}
              className="w-36"
            />
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((it, idx) => (
            <tr
              key={it.id}
              className={`border-b border-gray-100 hover:bg-gray-50/60 ${
                it.requestItemId === null ? 'bg-red-50/50' : ''
              }`}
            >
              <td className="px-3 py-1.5 text-center text-xs text-gray-500">{idx + 1}</td>
              <td className="px-3 py-1.5 font-mono text-xs">
                {it.requestItem ? (
                  <Link
                    to={`/request-items/${it.requestItem.id}`}
                    className="text-brand-600 hover:underline"
                  >
                    {it.partNumber}
                  </Link>
                ) : (
                  <span className="text-gray-800">{it.partNumber}</span>
                )}
              </td>
              <td className="px-3 py-1.5 text-gray-700">{it.brand ?? '—'}</td>
              <td className="px-3 py-1.5 text-right text-gray-700">{it.quantity}</td>
              <td className="px-3 py-1.5 text-xs text-gray-700">
                {it.responsePrice ? `${it.responsePrice} ${it.responseCurrency ?? ''}`.trim() : '—'}
              </td>
              <td className="px-3 py-1.5">
                {it.requestItemId === null ? (
                  <span
                    className="text-xs px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-600"
                    title="Заявка, к которой относилась позиция, была удалена. Ответ по этой строке привязать некуда."
                  >
                    Заявка удалена
                  </span>
                ) : (
                  <select
                    value={it.status}
                    onChange={(e) =>
                      onPatchStatus(it.id, e.target.value as ProcurementRequestItemStatus)
                    }
                    className={`text-xs px-2 py-0.5 rounded border ${ITEM_STATUS_TONE[it.status]} focus:outline-none`}
                  >
                    {(['PENDING', 'ANSWERED', 'REFUSED', 'NO_RESPONSE'] as const).map((s) => (
                      <option key={s} value={s}>
                        {ITEM_STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
